import { styled, Stack } from '@mui/material';
import { type ReactNode, forwardRef } from 'react';
import { VyCard } from '../../data-display/VyCard';
import { VyTypography } from '../../data-display/VyTypography';

/**
 * @tier organism
 *
 * Form section composition: icon + uppercase letter-spaced header outside
 * a card, 12px gap, then the card body. Used for the Pricing / Schedule /
 * Payment / Upgrade Options groupings in the Edit Product drawer.
 *
 * Net-new primitive (no 1:1 MUI parent), so styling lives here via
 * `styled()` reading from theme tokens — no hex, no `sx` for visuals.
 */
export type VyFormSectionProps = {
  /** Icon shown to the left of the title. Typically a lib `VyIcon`. */
  icon: ReactNode;
  /** Section heading. Rendered uppercase. */
  title: string;
  /** Optional right-aligned slot in the header (e.g. a `VyChip`). */
  action?: ReactNode;
  /** Card body content. */
  children: ReactNode;
};

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingInline: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const SectionRoot = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1.5),
}));

export const VyFormSection = forwardRef<HTMLDivElement, VyFormSectionProps>(
  function VyFormSection({ icon, title, action, children }, ref) {
    return (
      <SectionRoot ref={ref}>
        <Header>
          {icon}
          <VyTypography variant="sectionHeader" color="text.secondary">
            {title}
          </VyTypography>
          {action}
        </Header>
        <VyCard>{children}</VyCard>
      </SectionRoot>
    );
  },
);
